import { useCallback, useEffect, useState } from 'react';
import '../backlog/backlog.css';
import './reagentprep.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { LookupCombo } from '../../components/LookupCombo';
import { api } from '../../api/client';
import { useConfirm, useSaveFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';
import {
  createReagentPrep, deleteReagentPreps, fetchComponentCoa, fetchComponents, fetchLookup,
  fetchReagentPrep, generateLevels, saveComponents, saveLevels, updateReagentPrep,
  type CalibrationLevelDto, type PrepNotepadComponentDto,
} from './api';
import { CalibrationLevelsGrid } from './CalibrationLevelsGrid';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { CALIBRATION, CHEMICAL, MICRO, prepForm, type PrepField } from './prepForms';
import { LOOKUP_GEOMETRY } from './npFields';
import { chemColumnsWithCoa, MICRO_COLS } from './componentColumns';
import {
  NA_OPTION, SOLVENT_MODES, STOCK_SOLUTIONS,
  type ReagentLookupOption, type ReagentPrepComponent, type ReagentPrepDetail,
  type ReagentPrepInput,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation — the DETAIL view: all three legacy header forms
//   ReagentPreparation_DetailView_Chemistry / _MicroMedia / _Calibration
// rendered from the per-category definitions in prepForms.ts, which are
// transcribed from the Model.xafml LAYOUTS. The record's own prepSelectType
// picks the variant, exactly as EditAction_Executing (:767-790) picks which
// DetailView to create.
//
// NOTHING IS RENDERED THAT XAF DOES NOT RENDER. The field list, its column, its
// order, its caption and its read-only state all come from prepForms.ts; this
// component only knows how to draw a PrepField. That is what stops the three
// categories drifting apart, and what stops an extra control creeping in.
//
// ── Action bar (XAF TargetViewId map, ctrl :66-79 + the DetailViews' own
//    HiddenActions) ─────────────────────────────────────────────────────────
//   Save · Save and Close · Cancel   — all three
//   Prep Notepad                     — Chemistry + Micro Media only (:69)
//   New · Delete                     — all three
//   Save and New                     — HIDDEN: every one of the three
//                                      DetailViews declares
//                                      <ActionLink ActionId="SaveAndNew"/> under
//                                      <HiddenActions>. This is why the page
//                                      does not use rule 12's Save trio.
//   Ok                               — Calibration only, and it is an
//                                      ActionContainerViewItem INSIDE the
//                                      layout beside No Of Levels, NOT a
//                                      toolbar button.
//   Reports ▾                        — Chemistry + Calibration (the views
//                                      FormViews registers); the lab-assigned
//                                      menu that replaces the hidden Prep Report /
//                                      Preview Label buttons (:71/:73).
//
// Rendered top and bottom, but NOT identical (owner request 2026-10-16): the TOP
// bar carries the full set; the BOTTOM bar only Save · Save and Close · Cancel.
// New · Delete · Reports · Prep Notepad are top-only, in that order.
//
// ⚠️ RULE 12 DEVIATION, deliberate and flagged: bespoke rather than the shared
// <SettingsDetailPage>, because that component has no slot for the
// form-specific Prep Notepad action or the inline Ok, and because XAF hides
// Save & New. Same precedent as ADR-0015's SdmsSequenceSetupPage. It still
// composes the shared AppToolbar / AppDataGrid / useSaveFlow / useConfirm /
// useNotify, and reagentprep.css mirrors the shared detail page's geometry, so
// nothing here is a second design system.
// ─────────────────────────────────────────────────────────────────────────────

function blankPrep(prepSelectType: number): ReagentPrepDetail {
  return {
    oid: '', llt: null, prepSelectType,
    prepTypeId: null, prepTypeName: null, prepNameId: null, prepNameText: null,
    testIds: [], testText: null, departmentId: null, departmentName: null,
    divisionId: null, divisionName: null, expirationDate: null,
    preparedById: null, preparedByName: null, datePrepared: null, setupDate: null,
    solvent: 0, boolSolvent: false, solventID: null,
    storageId: null, storageName: null, comment: null,
    dateDepleted: null, depletedById: null,
    // Calibration is created with SelectStockSolution = VendorStock (spec §8).
    selectStockSolution: prepSelectType === CALIBRATION ? 0 : null,
    stockConc: null, stockConcUnitId: null, stockConcUnitName: null,
    vendorStockId: null, vendorStockName: null, ltId: null, ltText: null,
    labStockId: null, labStockName: null, lltLabStockId: null, lltLabStockText: null,
    calibrationSolventId: null, calibrationSolventName: null,
    calibrationSolventLtId: null, calibrationSolventLtText: null,
    noOfLevels: null,
  };
}

interface GridRow { id: string; cells: string[] }

interface Props {
  id: string | null;
  newPrepType?: number;
  /** Components entered in the Prep Notepad BEFORE this record existed (the
   *  Classify → New Prep → Chemical/Micro path). Written immediately after the
   *  first Save mints the header, because the Notepad runs with no parent. */
  pendingComponents?: PrepNotepadComponentDto[] | null;
  /** The same buffer shaped as grid rows, shown until the first Save writes it. */
  pendingRows?: ReagentPrepComponent[] | null;
  /** Bumped by the list page after the Notepad's OK writes this prep's
   *  components — the grid re-reads them without remounting the page. */
  componentsVersion?: number;
  onComponentsFlushed?: () => void;
  onClose: () => void;
  onNew: () => void;
  /** Chemistry / Micro Media only — opens the Prep Notepad for this record. */
  onPrepNotepad: (prep: ReagentPrepDetail, startComponentId?: number | null) => void;
  onCreated: (id: string) => void;
  onChanged: () => void;
  /** Prev / next record arrows (BO UI Standard §4) — the current page's ids. */
  siblingIds?: string[];
  onNavigate?: (id: string) => void;
}

export default function ReagentPrepDetailPage({
  id, newPrepType = CHEMICAL, pendingComponents = null, pendingRows = null, componentsVersion = 0, onComponentsFlushed,
  onClose, onNew, onPrepNotepad, onCreated, onChanged,
  siblingIds = [], onNavigate,
}: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const runSave = useSaveFlow();

  const [prep, setPrep]   = useState<ReagentPrepDetail>(() => blankPrep(newPrepType));
  const [comps, setComps] = useState<ReagentPrepComponent[]>([]);
  // Calibration levels grid: XAF batch edit, so typed values are BUFFERED here
  // and stored with the header's Save — never on their own.
  const [levelEdits, setLevelEdits] = useState<Record<string, Partial<ReagentPrepComponent>>>({});
  const levelsDirty = Object.keys(levelEdits).length > 0;
  // The shared tracker cannot see a grid buffer, so register it (standing rule 15).
  useUnsavedGuard(levelsDirty);
  const [loading, setLoading] = useState(id !== null);
  const [lookups, setLookups] = useState<Record<string, ReagentLookupOption[]>>({});
  // Grid-lookup feeds, keyed by "<lookup>|<parentId>" so a dependent picker
  // (LT# under Vendor Stock, Solvent LT# under Solvent) caches per parent
  // instead of clobbering its sibling.
  const [grids, setGrids] = useState<Record<string, GridRow[]>>({});

  const type  = prep.prepSelectType ?? newPrepType;
  const form  = prepForm(type);
  const isNew = id === null;

  const load = useCallback(async () => {
    if (id === null) return;
    setLoading(true);
    try {
      const [header, components] = await Promise.all([fetchReagentPrep(id), fetchComponents(id)]);
      setPrep(header);
      setComps(components);
      setLevelEdits({});
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the preparation.');
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { void load(); }, [load]);

  // After the Notepad's OK wrote this prep's components, re-read ONLY them —
  // the header (and any unsaved edits to it) stays as it is.
  useEffect(() => {
    if (componentsVersion === 0 || id === null) return;
    let live = true;
    fetchComponents(id)
      .then((c) => { if (live) setComps(c); })
      .catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to reload the components.'));
    return () => { live = false; };
  }, [componentsVersion, id, notify]);

  // Fetch exactly the lookups THIS category's form declares — no more. A
  // Chemistry form never loads the Calibration stock lists, and vice versa.
  useEffect(() => {
    const kinds = [...new Set([...form.col1, ...form.col2]
      .flatMap((f) => f.lookup && f.kind !== 'grid' && f.kind !== 'tests' ? [f.lookup] : []))];
    void (async () => {
      try {
        const loaded = await Promise.all(kinds.map(async (k) => [k, await fetchLookup(k)] as const));
        setLookups(Object.fromEntries(loaded));
      } catch (e) {
        notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      }
    })();
  }, [form, notify]);

  /** Fetch one grid-lookup. A dependent one with no parent returns empty,
   *  which is legacy behaviour (BackendLogic §0.4 "parent empty → SelectNothing"),
   *  so that case is silent; anything else is surfaced rather than swallowed. */
  const loadGrid = useCallback(async (key: string, kind: string, parentId?: string) => {
    try {
      const res = await api.get<{ data?: Array<{ id: string; name: string; cols?: string[] }> }>(
        `/api/v1/reagent-preps/grid-lookups/${kind}`,
        { params: parentId ? { parentId } : undefined },
      );
      const rows = res.data?.data;
      if (!Array.isArray(rows)) {
        throw new Error(`Lookup "${kind}" did not return data. The API may be out of date — restart it.`);
      }
      setGrids((g) => ({ ...g, [key]: rows.map((o) => ({ id: o.id, cells: [o.name, ...(o.cols ?? [])] })) }));
    } catch (e) {
      setGrids((g) => ({ ...g, [key]: [] }));
      notify.error(e instanceof Error ? e.message : `Failed to load the ${kind} list.`);
    }
  }, [notify]);

  // Load every grid-lookup this category declares, re-fetching a dependent one
  // whenever its parent changes.
  useEffect(() => {
    for (const fld of [...form.col1, ...form.col2]) {
      if (fld.kind !== 'grid' && fld.kind !== 'tests') continue;
      const parent = fld.parentKey
        ? ((prep as unknown as Record<string, unknown>)[fld.parentKey] as string | null) ?? undefined
        : undefined;
      if (fld.parentKey && !parent) { setGrids((g) => ({ ...g, [`${fld.lookup}|`]: [] })); continue; }
      void loadGrid(`${fld.lookup}|${parent ?? ""}`, fld.lookup!, parent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loadGrid, prep.vendorStockId, prep.labStockId, prep.calibrationSolventId]);

  function patch(p: Partial<ReagentPrepDetail>) { setPrep((cur) => ({ ...cur, ...p })); }

  /** Solvent = N/A clears SolventID and unticks BoolSolvent (spec §13).
   *  Mirrored for immediate feedback; the SERVER is authoritative and re-derives
   *  BoolSolvent on every save, so the two cannot disagree. */
  function setSolvent(v: number) {
    patch(v === 0 ? { solvent: 0, boolSolvent: false, solventID: null }
                  : { solvent: v, boolSolvent: true });
  }

  /** Calibration: the radio switches WHICH stock pair is visible, and clears
   *  the other one so a hidden field can never be saved (spec §12.1). */
  function setStockSolution(v: number) {
    patch(v === 0
      ? { selectStockSolution: 0, labStockId: null, lltLabStockId: null }
      : { selectStockSolution: 1, vendorStockId: null, ltId: null });
  }

  function toInput(): ReagentPrepInput {
    const cal = type === CALIBRATION;
    return {
      prepSelectType: type,
      prepTypeId: prep.prepTypeId, prepNameId: prep.prepNameId,
      // Calibration's form has no Test field, so it never sends one.
      testIds: cal ? null : prep.testIds,
      expirationDate: prep.expirationDate, datePrepared: prep.datePrepared,
      // ...and no enum Solvent / SolventID either.
      solvent: cal ? null : prep.solvent,
      solventID: cal ? null : prep.solventID,
      storageId: prep.storageId, comment: prep.comment,
      selectStockSolution: cal ? prep.selectStockSolution : null,
      stockConc: cal ? prep.stockConc : null,
      stockConcUnitId: cal ? prep.stockConcUnitId : null,
      vendorStockId: cal ? prep.vendorStockId : null,
      ltId: cal ? prep.ltId : null,
      labStockId: cal ? prep.labStockId : null,
      lltLabStockId: cal ? prep.lltLabStockId : null,
      calibrationSolventId: cal ? prep.calibrationSolventId : null,
      calibrationSolventLtId: cal ? prep.calibrationSolventLtId : null,
      noOfLevels: cal ? prep.noOfLevels : null,
    };
  }

  async function save(close: boolean) {
    // Spec §12.1 / §18 message 8, verbatim — Calibration refuses to save with
    // no levels. The server-side required-field rules are the authority (§14b.5).
    if (type === CALIBRATION && comps.length === 0) {
      notify.warning('Entered the no of level and click the save.');
      return;
    }
    let newId: string | null = null;
    await runSave({
      formName: 'Reagent Prep Log',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        const res = isNew ? await createReagentPrep(toInput())
                          : await updateReagentPrep(id!, toInput());
        newId = res.oid;
        patch({ llt: res.llt, oid: res.oid });
        // Levels after the header: the header save may push a CHANGED Stock
        // Conc into every level, and a level the analyst typed into in the
        // same Save must keep their value (XAF order of events).
        if (!isNew && type === CALIBRATION && levelsDirty) {
          await saveLevels(res.oid, levelDtos());
          setComps(await fetchComponents(res.oid));
          setLevelEdits({});
        }
        // The Notepad ran before this record existed, so its buffer lands now —
        // one write, immediately after the header is minted.
        if (isNew && pendingComponents && pendingComponents.length > 0) {
          await saveComponents(res.oid, pendingComponents);
          setComps(await fetchComponents(res.oid));
          onComponentsFlushed?.();
        }
      },
      onSaved: () => {
        onChanged();
        if (close) onClose();
        else if (isNew && newId) onCreated(newId);
      },
    });
  }

  /** The buffered level edits, as the API takes them — every edited level is
   *  sent whole, so the server sees its complete new state. */
  function levelDtos(): CalibrationLevelDto[] {
    return comps.filter((c) => levelEdits[c.oid]).map((c) => {
      const r = { ...c, ...levelEdits[c.oid] };
      return {
        oid: r.oid,
        workingStdName: r.workingStdName, stockStdName: r.stockStdName,
        cal_Weight_g_w1: r.cal_Weight_g_w1,       calWsUnitsId: r.calWsUnitsId ?? null,
        cal_VolumeTaken_V1: r.cal_VolumeTaken_V1, calV1UnitsId: r.calV1UnitsId ?? null,
        cal_FinalVol_V2: r.cal_FinalVol_V2,       calV2UnitsId: r.calV2UnitsId ?? null,
        cal_FinalConc_C2: r.cal_FinalConc_C2,     calC2UnitsId: r.calC2UnitsId ?? null,
        formula: r.formula, comment: r.comment,
      };
    });
  }

  function editLevels(oids: string[], p: Partial<ReagentPrepComponent>) {
    setLevelEdits((cur) => {
      const next = { ...cur };
      for (const o of oids) next[o] = { ...next[o], ...p };
      return next;
    });
  }

  async function remove() {
    if (isNew) { onClose(); return; }
    const ok = await confirm({
      title: 'Delete?', message: `Delete preparation ${prep.llt ?? ''}?`,
      tone: 'danger', confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteReagentPreps([id!]);
      notify.success('Deleted.');
      onChanged();
      onClose();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to delete the preparation.');
    }
  }

  /** Calibration [Ok] — spec §12.2. */
  async function makeLevels() {
    if (isNew) { notify.warning('Save the preparation before generating levels.'); return; }
    try {
      const count = await generateLevels(id!);
      setComps(await fetchComponents(id!));
      notify.success(`${count} level(s) ready.`);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to generate the levels.');
    }
  }

  // ── Field renderer — draws exactly what the PrepField says, nothing more ──
  /**
   * BRANCH_C: choosing the Items row named "Water" as the Calibration Solvent
   * makes Solvent LT# read-only — water needs no lot (BackendLogic C §2.C).
   *
   * Legacy never re-enables it when the solvent changes away from Water (a
   * documented bug in that same section); this recomputes it from the current
   * value each render, so picking a different solvent makes the field editable
   * again. The name test is against the item's display text, as in legacy.
   */
  const solventIsWater =
    (prep.calibrationSolventName ?? '').trim().toLowerCase() === 'water';

  function renderField(f: PrepField) {
    if (f.showWhen === 'vendorStock' && (prep.selectStockSolution ?? 0) !== 0) return null;
    if (f.showWhen === 'labStock'    && (prep.selectStockSolution ?? 0) !== 1) return null;
    const waterLocked = f.key === 'calibrationSolventLtId' && solventIsWater;

    const raw = (prep as unknown as Record<string, unknown>)[f.key];
    // Required-yet-empty gets the yellow tint (BO UI Standard §4 / rule 12).
    const empty = raw === null || raw === undefined || raw === '';
    // The shared .sd-input already paints EVERY editable field yellow (BO UI
    // Standard, app-wide since 2026-07-10). The modifier only adds the amber
    // BORDER a required-yet-empty field gets on top of that.
    const tint  = f.required && empty ? ' required-empty' : '';

    let control: React.ReactNode;
    switch (f.kind) {
      case 'readonly':
        control = <input type="text" className="sd-input readonly" readOnly
                         value={(raw as string | null) ?? ''} />;
        break;
      case 'text':
        control = <input type="text" className={`sd-input${tint}`} value={(raw as string | null) ?? ''}
                         // SolventID is editable only when Solvent ≠ N/A (§13).
                         readOnly={f.key === 'solventID' && !prep.boolSolvent}
                         onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'textarea':
        control = <textarea rows={f.rows ?? 3} className={`sd-input${tint}`} value={(raw as string | null) ?? ''}
                            onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'date':
      case 'datePast':
        control = <input type="date" className={`sd-input${tint}`}
                         // XAF disables future dates on DatePrepared (§18 msg 3).
                         max={f.kind === 'datePast' ? today() : undefined}
                         value={dateInput(raw as string | null)}
                         onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'number':
        control = <input type="number" step="any" className={`sd-input${tint}`} value={(raw as number | null) ?? ''}
                         onChange={(e) => patch({ [f.key]: e.target.value === '' ? null : Number(e.target.value) } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'lookup':
        control = (
          <select className={`sd-input${tint}${waterLocked ? ' readonly' : ''}`}
                  disabled={waterLocked}
                  value={(raw as string | null) ?? ''}
                  onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)}>
            {/* N/A: client-only placeholder — '' is saved as null, never as text. */}
            <option value="">{NA_OPTION.label}</option>
            {(lookups[f.lookup!] ?? []).map((o) => <option key={o.id} value={o.id}>{o.name ?? ''}</option>)}
          </select>
        );
        break;
      case 'grid': {
        const parent = f.parentKey
          ? ((prep as unknown as Record<string, unknown>)[f.parentKey] as string | null) ?? ""
          : "";
        const rows = grids[`${f.lookup}|${parent}`] ?? [];
        const shown = f.textKey
          ? ((prep as unknown as Record<string, unknown>)[f.textKey] as string | null) ?? ""
          : "";
        // Rows are identified by their OWN Oid, never by the name in column 0:
        // names repeat (one standard name has a row per prep type), and keying by
        // name highlighted — and could pick — every row sharing it.
        const idOfRow = new Map(rows.map((r) => [r.cells, r.id] as const));
        const selectedId = ((prep as unknown as Record<string, unknown>)[f.key] as string | null) ?? '';
        control = (
          <LookupCombo
            lookup={{ cols: f.cols ?? ['Name'], rows: rows.map((r) => r.cells) }}
            value={selectedId}
            rowKey={(row) => idOfRow.get(row) ?? ''}
            labelText={shown}
            disabled={waterLocked}
            popupWidth={LOOKUP_GEOMETRY[f.lookup!]?.popup}
            colWidths={LOOKUP_GEOMETRY[f.lookup!]?.widths}
            wrapCells
            className={tint ? tint.trim() : undefined}
            // N/A first: clears this pick and any lookup that depends on it
            // (LT# under Vendor Stock, LLT# under Lab Stock, Solvent LT# under Solvent).
            naOption
            // Plain text value — no grey label chip (owner request 2026-10-16).
            plainValue
            onClear={() => {
              const next: Record<string, null> = { [f.key]: null };
              if (f.textKey) next[f.textKey] = null;
              for (const dep of [...form.col1, ...form.col2]) {
                if (dep.parentKey !== f.key) continue;
                next[dep.key] = null;
                if (dep.textKey) next[dep.textKey] = null;
              }
              patch(next as Partial<ReagentPrepDetail>);
            }}
            onPick={(row) => {
              // By row IDENTITY only — never fall back to the name, which repeats.
              const found = rows.find((r) => r.cells === row) ?? null;
              const next: Partial<ReagentPrepDetail> = {
                [f.key]: found?.id ?? null,
                ...(f.textKey ? { [f.textKey]: row[0] ?? null } : {}),
              } as Partial<ReagentPrepDetail>;
              // Mutual exclusion (BackendLogic C §2.B/§2.E): choosing one
              // stock side clears the other, together with its lot.
              if (f.key === 'vendorStockId')
                Object.assign(next, { labStockId: null, labStockName: null, lltLabStockId: null, lltLabStockText: null });
              if (f.key === 'labStockId')
                Object.assign(next, { vendorStockId: null, vendorStockName: null, ltId: null, ltText: null });
              patch(next);
            }}
          />
        );
        break;
      }
      case 'tests': {
        // XAF's checked grid-lookup: the ticked rows are the TestMethod Oids, the
        // closed combo shows "TestName | MethodNumber" (TextFormatString {2} | {3}).
        const rows = grids['tests|'] ?? [];
        const idOf = new Map(rows.map((r) => [r.cells, r.id] as const));
        const chosen = new Set(prep.testIds ?? []);
        const shown = rows.filter((r) => chosen.has(r.id))
          .map((r) => `${r.cells[1] ?? ''} | ${r.cells[2] ?? ''}`).join(';');
        control = (
          <LookupCombo
            multi
            naOption
            // Plain text value — no grey label chip (owner request 2026-10-16).
            plainValue
            lookup={{ cols: f.cols ?? ['Name'], rows: rows.map((r) => r.cells) }}
            rowKey={(row) => idOf.get(row) ?? row.join('|')}
            values={prep.testIds ?? []}
            labelText={shown || prep.testText || ''}
            popupWidth={560}
            colWidths={[150, 220, 150]}
            wrapCells
            className={tint ? tint.trim() : undefined}
            onToggle={(next) => patch({ testIds: next })}
          />
        );
        break;
      }
      case 'enum':
        control = (
          <select className="sd-input" value={prep.solvent ?? 0}
                  onChange={(e) => setSolvent(Number(e.target.value))}>
            {SOLVENT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        );
        break;
      case 'radio':
        control = (
          <div className="rgp-radiorow" role="radiogroup" aria-label={f.label}>
            {STOCK_SOLUTIONS.map((s) => (
              <label key={s.value} className="rgp-radio">
                <input type="radio" name="rgp-stock"
                       checked={(prep.selectStockSolution ?? 0) === s.value}
                       onChange={() => setStockSolution(s.value)} />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        );
        break;
      case 'checkbox':
        // BoolSolvent: AllowEdit="False", Caption="" — read-only, no caption,
        // no added text of our own.
        control = <input type="checkbox" checked={!!prep.boolSolvent} readOnly disabled
                         aria-label="Solvent" />;
        break;
      case 'levels':
        // NoOfLevels + the inline [Ok] ActionContainerViewItem — ONE layout row,
        // exactly as Model.xafml nests them in a horizontal LayoutGroup.
        control = (
          <div className="rgp-inline">
            <input type="number" className="sd-input" min={0} value={prep.noOfLevels ?? ''}
                   onChange={(e) => patch({ noOfLevels: e.target.value === '' ? null : Number(e.target.value) })} />
            <button type="button" className="ca-btn" onClick={() => void makeLevels()} disabled={isNew}>
              Ok
            </button>
          </div>
        );
        break;
    }

    return (
      <div className={`rgp-field${f.label === '' ? ' rgp-field--nolabel' : ''}`} key={f.key}>
        {/* Shared .sd-label + .req — the same label styling as every other form. */}
        <label className="rgp-label sd-label">
          {f.label}{f.required && <span className="req" aria-hidden="true">*</span>}
        </label>
        <div className="rgp-control">{control}</div>
      </div>
    );
  }

  // ── Action bar, identical top and bottom ─────────────────────────────────
  // TOP bar = the full set; BOTTOM bar = the Save group only (owner request
  // 2026-10-16: New · Delete · Reports belong to the top bar alone).
  const actions = (top: boolean) => (
    <AppToolbar
      // Save · Save and Close · Cancel sit at the RIGHT edge, as on the shared
      // detail page (.sd-actionbar is justify-content:flex-end); the form's own
      // actions stay on the left. `app-toolbar__spacer` on the first of the group
      // pushes it and its siblings right (same pattern as Control Chart).
      right={
        <>
          <button type="button" className="ca-btn ca-btn-solid app-toolbar__spacer"
                  onClick={() => void save(false)} disabled={loading}>
            <i className="ti ti-device-floppy" aria-hidden="true" /> Save
          </button>
          <button type="button" className="ca-btn"
                  onClick={() => void save(true)} disabled={loading}>
            <i className="ti ti-check" aria-hidden="true" /> Save and Close
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>
            <i className="ti ti-x" aria-hidden="true" /> Cancel
          </button>
        </>
      }
      left={
        <>
          {top && (
            <>
              <button type="button" className="ca-btn" onClick={onNew}>
                <i className="ti ti-plus" aria-hidden="true" /> New
              </button>
              <button type="button" className="ca-btn" onClick={() => void remove()} disabled={isNew}>
                <i className="ti ti-trash" aria-hidden="true" /> Delete
              </button>
              {/* Reports ▾ for the OPEN record — legacy registers it on the Chemistry and
                  Calibration detail views only (dbo.FormViews has no Micro detail view). */}
              {!isNew && prep.llt && type !== MICRO && (
                <FormReportsMenu
                  reportsUrl={`/api/v1/reagent-preps/reports?view=${type === CALIBRATION ? 'calibration' : 'chemistry'}`}
                  pdfUrl="/api/v1/reagent-preps/report-pdf"
                  keysParam="llts"
                  selectedKeys={[prep.llt]}
                />
              )}
              {/* Prep Notepad — LAST on the top bar, after Reports; top bar only
                  (owner request 2026-10-16). Chemistry + Micro Media only (:69). */}
              {form.hasPrepNotepad && (
                <button type="button" className="ca-btn" onClick={() => onPrepNotepad(prep)}>
                  <i className="ti ti-notebook" aria-hidden="true" /> Prep Notepad
                </button>
              )}
            </>
          )}
        </>
      }
    />
  );

  /** COA "Attachment" on a component row (XAF PreviewCOA_Reagent) — opens the LT
   *  vendor certificate or the LLT notebook in a new tab; nothing to show → say so. */
  async function previewCoa(row: ReagentPrepComponent) {
    if (!id) return;
    try {
      const blob = await runLoading(fetchComponentCoa(id, row.oid), { blocking: true, label: 'Opening the COA…' });
      if (!blob) { notify.info('No COA is available for this component.'); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to open the COA.');
    }
  }

  const childCols: AppColumn<ReagentPrepComponent>[] =
    type === MICRO ? MICRO_COLS : chemColumnsWithCoa((row) => void previewCoa(row));
  const levelRows = comps.map((c) => (levelEdits[c.oid] ? { ...c, ...levelEdits[c.oid] } : c));

  // Prev / next record arrows (BO UI Standard §4).
  const pos  = id ? siblingIds.indexOf(id) : -1;
  const prev = pos > 0 ? siblingIds[pos - 1] : null;
  const next = pos >= 0 && pos < siblingIds.length - 1 ? siblingIds[pos + 1] : null;

  return (
    <div className="bl-page rgp-detail">
      <div className="rgp-head">
        <div className="rgp-head-main">
          <h2>
            {/* {BO} – {record}, per the BO UI Standard header. XAF's own
                DetailView Caption is the BO half. */}
            {form.caption}
            {' – '}
            {/* XAF sets NullText "Autogenerate" on LLT until the first save (:534). */}
            <span className="rgp-llt">{prep.llt ?? 'Autogenerate'}</span>
          </h2>
          <div className="rgp-crumb">
            Reagent Preparation › Reagent Prep Log › {prep.llt ?? 'New'}
          </div>
        </div>
        {onNavigate && siblingIds.length > 1 && (
          <div className="rgp-recnav">
            <button type="button" className="ca-btn" aria-label="Previous record"
                    disabled={!prev} onClick={() => prev && onNavigate(prev)}>
              <i className="ti ti-chevron-left" aria-hidden="true" />
            </button>
            <button type="button" className="ca-btn" aria-label="Next record"
                    disabled={!next} onClick={() => next && onNavigate(next)}>
              <i className="ti ti-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {actions(true)}

      <div className="rgp-card">
        <div className="rgp-cap">Basic Information</div>
        <div className="rgp-cols">
          <div className="rgp-col">{form.col1.map(renderField)}</div>
          <div className="rgp-col">{form.col2.map(renderField)}</div>
        </div>
      </div>

      <div className="rgp-card">
        {/* Only Chemistry's child group carries a caption in XAF; the other two
            set ShowCaption="False". */}
        {form.childCaption && <div className="rgp-cap">{form.childCaption}</div>}
        {type === CALIBRATION ? (
          <CalibrationLevelsGrid
            prepId={id}
            prep={prep}
            rows={levelRows}
            units={lookups.units ?? []}
            loading={loading}
            onEdit={editLevels}
          />
        ) : (
          <AppDataGrid<ReagentPrepComponent>
            rows={isNew && pendingRows ? pendingRows : comps}
            columns={childCols}
            getRowId={(r) => r.oid}
            loading={loading}
            emptyText=""
            // A row click opens the Prep Notepad ON that component, its data filled.
            onRowClick={!isNew && form.hasPrepNotepad
              ? (r) => onPrepNotepad(prep, r.componentID == null ? null : Number(r.componentID))
              : undefined}
          />
        )}
      </div>

      {actions(false)}
    </div>
  );
}

/** A wire date → the `yyyy-MM-dd` an <input type="date"> needs. Goes through the
 *  shared helper, so no date formatting is inlined here (C2). */
function dateInput(v: string | null): string {
  if (!v) return '';
  const [m, d, y] = fmtLocalDate(v).split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function today(): string {
  const [m, d, y] = fmtLocalDate(new Date().toISOString()).split('/');
  return `${y}-${m}-${d}`;
}
